import { Dialog, Transition } from '@headlessui/react'
import { XCircleIcon } from '@heroicons/react/outline'
import { Fragment, useRef } from 'react'

type Props = {
  title: string
  children: React.ReactNode
  isOpen: boolean
  handleClose: () => void
  // When true, the modal cannot be dismissed: no X button, and Escape /
  // backdrop clicks do not close it. Used for the stats/leaderboard modal.
  hideClose?: boolean
  // Backdrop appears at full strength with no fade (e.g. straight after the
  // Save the Date card, when the screen is already dark); the panel still
  // eases in so it cross-fades with whatever was on top.
  instant?: boolean
}

// Panels that already played their instant-mode entrance.
const entered = new WeakSet<Element>()

export const BaseModal = ({
  title,
  children,
  isOpen,
  handleClose,
  hideClose = false,
  instant = false,
}: Props) => {
  // Focus the panel itself on open, so the first button doesn't open with a
  // focus ring drawn around it. The panel is the first tab stop (tabIndex 0)
  // because Headless UI's focus trap falls back to the first *tabbable*
  // element; keyboard users press Tab once to reach the buttons.
  const panelRef = useRef<HTMLDivElement | null>(null)

  // With an instant backdrop, ease the panel in ourselves (Headless UI's
  // class transition doesn't reliably run in this case). Done from the ref
  // callback because the panel mounts a frame after isOpen flips (portal);
  // the element is fresh each open, so this runs once per open.
  const attachPanel = (el: HTMLDivElement | null) => {
    panelRef.current = el
    // (React re-calls this on every render; animate each panel only once.)
    if (!el || !instant || entered.has(el)) return
    entered.add(el)
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    el.animate(
      [
        { opacity: 0, transform: 'translateY(12px) scale(0.98)' },
        { opacity: 1, transform: 'translateY(0) scale(1)' },
      ],
      { duration: 320, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
    )
  }

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-10 overflow-y-auto"
        initialFocus={panelRef}
        onClose={hideClose ? () => {} : handleClose}
      >
        <div className="flex min-h-full items-center justify-center py-10 px-4 text-center sm:p-0">
          <Transition.Child
            as={Fragment}
            enter={instant ? '' : 'ease-out duration-300'}
            enterFrom={instant ? '' : 'opacity-0'}
            enterTo={instant ? '' : 'opacity-100'}
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 min-h-screen bg-black/70 transition-opacity" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            // instant: the panel's entrance is the animation above instead
            enter={instant ? '' : 'ease-out duration-300'}
            enterFrom={
              instant
                ? ''
                : 'opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            }
            enterTo={instant ? '' : 'opacity-100 translate-y-0 sm:scale-100'}
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              ref={attachPanel}
              tabIndex={0}
              className="inline-block transform overflow-hidden rounded-2xl bg-white px-4 pt-6 pb-5 text-left align-bottom shadow-xl outline-none transition-all dark:bg-slate-800 sm:my-8 sm:w-full sm:max-w-sm sm:p-6 sm:align-middle"
            >
              {!hideClose && (
                <button
                  onClick={() => handleClose()}
                  tabIndex={0}
                  aria-pressed="false"
                  className="absolute right-4 top-4"
                >
                  <XCircleIcon className="h-6 w-6 cursor-pointer stroke-gray-500 hover:stroke-gray-900 dark:stroke-gray-300 dark:hover:stroke-white" />
                </button>
              )}
              <div>
                <div className="text-center">
                  <Dialog.Title
                    as="h3"
                    className="text-xl font-extrabold leading-7 tracking-tight text-gray-900 dark:text-white"
                  >
                    {title}
                  </Dialog.Title>
                  <div className="mt-2">{children}</div>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
